import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { IdCard, Plus, Trash2, Save, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

/**
 * Iter 65 — advisor self-edit of compliance/contact details used on the
 * Advisor Disclosure Statement: licences (type/province/number), other occupation,
 * business address, phone, email.
 */
const AdvisorComplianceProfile = ({ agent, onUpdate }) => {
  const [phone, setPhone] = useState(agent.phone || '');
  const [email, setEmail] = useState(agent.email || '');
  const [businessAddress, setBusinessAddress] = useState(agent.business_address || '');
  const [otherOccupation, setOtherOccupation] = useState(agent.other_occupation || '');
  const [licenses, setLicenses] = useState(
    (agent.licenses_detail && agent.licenses_detail.length)
      ? agent.licenses_detail
      : [{ type: '', province: '', number: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const setLic = (i, k, v) => setLicenses(prev => prev.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLic = () => setLicenses(prev => [...prev, { type: '', province: '', number: '' }]);
  const removeLic = (i) => setLicenses(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleanLicenses = licenses.filter(l => l.type || l.province || l.number);
      const body = {
        phone, email, business_address: businessAddress,
        other_occupation: otherOccupation, licenses_detail: cleanLicenses,
      };
      const res = await fetch(`${API_URL}/api/agents/${agent.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      const data = await res.json();
      const updated = data.agent || {};
      setSavedAt(new Date());
      // Refresh session + parent so the disclosure PDF picks up the new details.
      const merged = { ...agent, ...updated };
      delete merged.password_hash;
      sessionStorage.setItem('agent_session', JSON.stringify(merged));
      if (onUpdate) onUpdate(merged);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="advisor-compliance-profile">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IdCard className="h-5 w-5 text-[#0077B6]" /> Licences & Compliance Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-gray-500">These details appear on your Advisor Disclosure Statement in every case file.</p>

        {/* Licences */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700">Licences</label>
            <Button size="sm" variant="outline" onClick={addLic} data-testid="add-license-btn"><Plus className="h-4 w-4 mr-1" />Add licence</Button>
          </div>
          <div className="space-y-2">
            {licenses.map((l, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                <input
                  className="sm:col-span-5 px-3 py-2 border rounded-lg text-sm"
                  placeholder="Type of licence (e.g., Life / A&S, Mortgage)"
                  value={l.type || ''} onChange={(e) => setLic(i, 'type', e.target.value)}
                  data-testid={`license-type-${i}`}
                />
                <select
                  className="sm:col-span-3 px-3 py-2 border rounded-lg text-sm bg-white"
                  value={l.province || ''} onChange={(e) => setLic(i, 'province', e.target.value)}
                  data-testid={`license-province-${i}`}
                >
                  <option value="">Province</option>
                  {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input
                  className="sm:col-span-3 px-3 py-2 border rounded-lg text-sm"
                  placeholder="Licence #"
                  value={l.number || ''} onChange={(e) => setLic(i, 'number', e.target.value)}
                  data-testid={`license-number-${i}`}
                />
                <button onClick={() => removeLic(i)} className="sm:col-span-1 text-rose-500 hover:text-rose-700 flex justify-center" title="Remove" data-testid={`remove-license-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
          <input className="w-full px-3 py-2 border rounded-lg text-sm" value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} placeholder="Street, City, Province, Postal Code" data-testid="profile-business-address" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Other Occupation</label>
          <input className="w-full px-3 py-2 border rounded-lg text-sm" value={otherOccupation} onChange={(e) => setOtherOccupation(e.target.value)} placeholder="e.g., Realtor, Mortgage Broker (used for conflict-of-interest disclosure)" data-testid="profile-other-occupation" />
        </div>

        <div className="flex items-center gap-3 pt-2 border-t">
          <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005A8C]" data-testid="profile-save-btn">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save Details'}
          </Button>
          {savedAt && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" />Saved</span>}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdvisorComplianceProfile;
